import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { campaignFinanceReviews, decideCampaignFinance, type CampaignDraft } from './api';
const button =
  'min-h-11 rounded-lg border border-border-subtle px-4 py-2 text-sm font-semibold disabled:opacity-40';
const field = 'mt-1 w-full rounded-lg border border-border-subtle bg-bg-base px-3 py-2';
const money = (value: number | null) =>
  value === null
    ? 'Nicht berechenbar'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value / 100);
export function CampaignFinancePanel({
  row,
  canFinance,
}: {
  row: CampaignDraft;
  canFinance: boolean;
}) {
  const client = useQueryClient();
  const key = ['campaign-finance-reviews', row.app, row.environment, row.id, row.version];
  const query = useQuery({
    queryKey: key,
    queryFn: () => campaignFinanceReviews(row),
    retry: false,
  });
  const [source, setSource] = useState(''),
    [reason, setReason] = useState(''),
    [confirmed, setConfirmed] = useState(false);
  const [intent, setIntent] = useState<'CONFIRM' | 'WITHDRAW' | null>(null);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [message, setMessage] = useState('');
  const data = query.data;
  const fresh = data?.draftVersion === row.version && !query.isFetching && !query.error;
  const decide = async () => {
    if (
      !intent ||
      !data ||
      !fresh ||
      !confirmed ||
      source.trim().length < 10 ||
      reason.trim().length < 10
    )
      return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await decideCampaignFinance(row, intent, data.reviewVersion, source.trim(), reason.trim());
      setMessage(
        intent === 'CONFIRM'
          ? 'Finanzprüfung bestätigt. Die Kampagne ist weiterhin nicht aktiviert.'
          : 'Finanzbestätigung zurückgezogen. Der frühere Beleg bleibt erhalten.',
      );
      void client.invalidateQueries({ queryKey: ['campaign-audit'] });
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Finanzentscheidung konnte nicht gespeichert werden.',
      );
    } finally {
      setIntent(null);
      setConfirmed(false);
      await client.invalidateQueries({ queryKey: key });
      setBusy(false);
    }
  };
  return (
    <section
      aria-label="Finanzprüfung"
      className="rounded-lg border border-border-subtle p-4 text-sm"
    >
      <h3 className="font-semibold">Finanzprüfung</h3>
      <p className="mt-2 text-text-secondary">
        Die zuständige Person prüft die Kostenquellen und bestätigt beide Stores ausdrücklich. Jede
        neue Entwurfsversion entwertet die Bestätigung. Geräteprüfung, Empfängerprüfung und
        Aktivierung folgen getrennt.
      </p>
      {query.isLoading && <p role="status">Finanzstand wird geladen …</p>}
      {query.error && <p role="alert">Finanzstand konnte nicht geladen werden.</p>}
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
      {data && (
        <>
          <p className="mt-3 font-semibold">
            {
              {
                NOT_REVIEWED: 'Noch nicht finanziell geprüft',
                CONFIRMED: 'Finanzprüfung bestätigt',
                WITHDRAWN: 'Finanzbestätigung zurückgezogen',
                SUPERSEDED: 'Durch eine neuere Entwurfsversion entwertet',
                EXPIRED_OR_INVALID: 'Grundlagen oder Kampagnenzeitraum nicht mehr gültig',
              }[data.status]
            }
          </p>
          <ul className="mt-2 space-y-1">
            {data.calculation.providers.map((p) => (
              <li key={p.provider}>
                {p.provider === 'APP_STORE' ? 'Apple' : 'Google'}: monatlicher Plan-Deckungsbeitrag{' '}
                {money(p.monthlyContributionLowerMinor)} · Mindestwert{' '}
                {money(p.minimumContributionMinor)} ·{' '}
                {p.thresholdMet === true
                  ? 'rechnerisch erreicht'
                  : p.thresholdMet === false
                    ? 'nicht erreicht'
                    : 'offen'}
              </li>
            ))}
          </ul>
          {!data.confirmable && (
            <p className="mt-2 text-warning">
              Vor einer Bestätigung vollständige, gültige Kostenquellen für beide Stores im Entwurf
              hinterlegen und den Mindestdeckungsbeitrag erreichen.
            </p>
          )}
          {!fresh && !query.isFetching && (
            <p className="mt-2 text-warning">Aktuellen Entwurf und Finanzstand neu laden.</p>
          )}
        </>
      )}
      {!canFinance && (
        <p className="mt-2 text-warning">
          Für diese App ist dir kein ausdrückliches Finanzprüfrecht zugewiesen.
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          className={button}
          disabled={busy || query.isFetching}
          onClick={() => {
            setIntent(null);
            setMessage('');
            void query.refetch();
          }}
        >
          Finanzstand neu laden
        </button>
        {canFinance && data && (
          <>
            <button
              type="button"
              className={button}
              disabled={busy || !fresh || !data.confirmable || data.status === 'CONFIRMED'}
              onClick={() => {
                setIntent('CONFIRM');
                setConfirmed(false);
                setError('');
                setMessage('');
              }}
            >
              Finanzprüfung bestätigen
            </button>
            {data.items[0]?.decision === 'CONFIRM' && (
              <button
                type="button"
                className={button}
                disabled={busy || !fresh}
                onClick={() => {
                  setIntent('WITHDRAW');
                  setConfirmed(false);
                  setError('');
                  setMessage('');
                }}
              >
                Finanzbestätigung zurückziehen
              </button>
            )}
          </>
        )}
      </div>
      {intent && (
        <div
          role="group"
          aria-label="Finanzentscheidung bestätigen"
          className="mt-3 space-y-3 rounded-lg bg-bg-base p-4"
        >
          <p>
            {intent === 'CONFIRM'
              ? 'Ich habe die Quellen, Nettozuflüsse, variablen Kosten und Mindestdeckungsbeiträge für Apple und Google fachlich geprüft.'
              : 'Ich ziehe die bisherige Finanzbestätigung dieses Entwurfs zurück.'}{' '}
            Dies erteilt keine Kampagnenfreigabe.
          </p>
          <label className="block">
            Quellenverweis der Finanzprüfung
            <input
              className={field}
              value={source}
              maxLength={300}
              disabled={busy}
              onChange={(e) => setSource(e.target.value)}
            />
          </label>
          <label className="block">
            Begründung der Finanzentscheidung
            <textarea
              className={field}
              value={reason}
              maxLength={500}
              disabled={busy}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <p className="text-text-secondary">
            Interne Belegreferenz und Begründung mit jeweils mindestens 10 Zeichen. Keine
            Zugangsdaten eintragen.
          </p>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={confirmed}
              disabled={busy}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            {intent === 'CONFIRM'
              ? 'Diese fachliche Finanzprüfung für beide Stores verbindlich bestätigen.'
              : 'Diese Finanzbestätigung verbindlich zurückziehen.'}
          </label>
          <button
            type="button"
            className={button}
            disabled={
              busy || !fresh || !confirmed || source.trim().length < 10 || reason.trim().length < 10
            }
            onClick={() => void decide()}
          >
            {intent === 'CONFIRM' ? 'Finanzbestätigung speichern' : 'Rücknahme speichern'}
          </button>
          <button
            type="button"
            className={`${button} ml-2`}
            disabled={busy}
            onClick={() => setIntent(null)}
          >
            Abbrechen
          </button>
        </div>
      )}
      {data?.items.length ? (
        <details className="mt-3">
          <summary>Finanzentscheidungen und Belegbindung</summary>
          {data.items.map((item) => (
            <div key={item.id} className="mt-2 rounded-lg bg-bg-base p-3">
              <p>
                Entscheidung {item.version} ·{' '}
                {item.decision === 'CONFIRM' ? 'Bestätigt' : 'Zurückgezogen'} · Entwurfsversion{' '}
                {item.draftVersion} · {new Date(item.createdAt).toLocaleString('de-DE')}
              </p>
              <p>{item.manifest.sourceReference}</p>
              <p>{item.manifest.reason}</p>
              <p className="break-all">SHA256: {item.manifestHash}</p>
            </div>
          ))}
          {data.hasMore && <p>Die letzten 20 Entscheidungen werden gezeigt.</p>}
        </details>
      ) : null}
    </section>
  );
}
